import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { IonButtons, IonContent, IonHeader, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import moment from 'moment';
import { Flexibility, Question } from 'src/app/services/slip.service';
import { ModalBackButtonComponent } from '../../modal-back-button/modal-back-button.component';
import { ComponentStatus } from '../student-slip-modal.store';

export interface SummaryItemViewModel {
  renderAs: 'text' | 'list';
  question: string;
  flexibility: Flexibility;
  textValue?: string;
  listValue?: { items: { legend: string; selected?: boolean }[] };
}

@Component({
  selector: 'app-student-slip-questions-summary',
  templateUrl: './student-slip-questions-summary.component.html',
  styleUrls: ['./student-slip-questions-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalBackButtonComponent, NgClass, IonHeader, IonToolbar, IonButtons, IonTitle, IonContent]
})
export class StudentSlipQuestionsSummaryComponent implements OnInit {
  @Input() redacted: boolean;
  @Input() status: ComponentStatus;
  @Input() items: { question: Question; response: any }[];
  summaryItems: SummaryItemViewModel[];
  title: string;
  banner: string;

  constructor(private modalController: ModalController) {}

  private createTitle(): string {
    switch (this.status) {
      case 'done':
        return this.redacted ? 'View Questions' : 'View Responses';
      case 'restricted':
        return 'Preview';
      case 'expired':
      case 'void':
      default:
        return 'View Questions';
    }
  }

  private createBanner(): string {
    switch (this.status) {
      case 'done':
        return this.redacted ? 'Responses submitted by other contacts are not displayed.' : null;
      case 'restricted':
        return 'You are not yet able to answer the questions section. Please submit a response to the consent request before continuing.';
      case 'expired':
        return 'You are no longer able to answer the questions section. This slip has now closed.';
      case 'void':
        return 'You are no longer able to answer the questions section. The consent needed for this slip has been declined.';
      default:
        return null;
    }
  }

  ngOnInit(): void {
    this.title = this.createTitle();
    this.banner = this.createBanner();
    this.summaryItems = this.items.map((i) => {
      switch (i.question.answerType) {
        case 'date':
          return {
            renderAs: 'text',
            question: i.question.questionWording,
            flexibility: i.question.flexibility,
            textValue: i.response ? moment(i.response).format('DD MMM yy') : '-'
          };
        case 'short-text':
        case 'long-text':
        case 'email':
        case 'phone':
        case 'number':
          return {
            renderAs: 'text',
            question: i.question.questionWording,
            flexibility: i.question.flexibility,
            textValue: i.response ?? '-'
          };
        case 'yes-no':
          return {
            renderAs: 'list',
            question: i.question.questionWording,
            flexibility: i.question.flexibility,
            listValue: {
              items: ['Yes', 'No'].map((o, index) => ({
                legend: o,
                selected: i.response?.label === o
              }))
            }
          };
        case 'multiple-choice': {
          const options = [...i.question.options.answerOptions];
          if (i.question.options.hasOther) options.push('Other');
          if (i.question.options.isMultiSelect) {
            return {
              renderAs: 'list',
              question: i.question.questionWording,
              flexibility: i.question.flexibility,
              listValue: {
                items: options.map((o, index) => ({
                  legend: o === 'Other' && i.response?.otherContent ? `${o} - ${i.response?.otherContent}` : o,
                  selected: !!i.response?.checked.find(c => c.label === o)
                }))
              }
            };
          } else {
            return {
              renderAs: 'list',
              question: i.question.questionWording,
              flexibility: i.question.flexibility,
              listValue: {
                items: options.map((o, index) => ({
                  legend: o === 'Other' && i.response?.otherContent ? `${o} - ${i.response?.otherContent}` : o,
                  selected: i.response?.label === o
                }))
              }
            };
          }
        }
        default:
          return null;
      }
    });
  }
  onDismiss(): void {
    this.modalController.dismiss();
  }
}
